/**
 * The seat: where a governor sits, and the only thing that holds both halves.
 *
 * `ResidentGovernor` decides and cannot reach a district. `SurfaceSim` is a
 * district and has never heard of a polity. This class is the join, and it does
 * exactly three things — assemble a reading, ask the policy, apply what comes back —
 * which is conventions §1 (*"a manager is handed a reading, not a district"*) applied
 * to the actor above rather than to a subsystem inside.
 *
 * ## Why the seat is where the verb/dial distinction lives
 *
 * Conventions §3: *physics publishes, dials are told, actors act.* A governor's
 * moves are two of those three kinds at once, and which kind a given move is has
 * nothing to do with the policy that chose it:
 *
 * | Move | Channel | Why |
 * |---|---|---|
 * | `tithe` | `SET_TITHE` through a `CommandIssuer` | charged once against a whole season, so *which tick* it changed on changes the money (ADR-021 D133) |
 * | `site` | `FORCE_PLACE` through the same issuer | a slab is a fact about the map at a tick, and a replay that lost the moment builds a different district |
 * | `freight` | `SurfaceSim.setFreight` | re-derived every cycle, so a restored district hauls what it is offered rather than replaying an instruction |
 * | `prices` | `SurfaceSim.postMarket` | the world publishes; nobody decided it at a tick (ADR-025 D157) |
 *
 * A policy that called the simulation itself would have had to know all four rows,
 * in every implementation anybody ever writes — including the LLM loop roadmap item
 * 2 names. Here it knows none of them.
 *
 * ## Why the seat observes rather than being told
 *
 * There is no season event on the bus and none is wanted: ADR-023's client raises
 * its card by noticing the Chronicle got longer, for the reason written there — *a
 * client the simulation had to tell is a client whose absence the simulation could
 * notice.* A governor is a stronger case of the same argument, because a governor
 * changes what happens. If a district emitted "season closed" and a governor were
 * *woken* by it, the district would contain a reference to the polity above it, and
 * §11's absence machinery — where a district settles the same whether anybody is
 * home — would be arguing against the shape of its own event bus.
 *
 * So: a length comparison, once a tick, exactly like the client's. The governor is
 * an observer that happens to act.
 *
 * ## What the seat is not
 *
 * Not simulation state. Nothing here is snapshotted, hashed or versioned: the
 * policy is stateless by construction and the only field the seat carries is how
 * many seasons it has already opened, which is a fact about *the seat* and is
 * re-synced from the record on a restore (`resync`). A district's snapshot already
 * holds every consequence — the rate in force, the slab on the ground, the legs
 * being hauled — so a governed world reloads governed, and no save format moved to
 * make that true.
 */

import { CommandIssuer, LOCAL_GOVERNOR } from '@antification/protocol';
import { FACILITY_SPECS } from '../surface/FacilityManager';
import { CellState } from '../surface/TerrainGrid';
import { SurfaceSim } from '../surface/SurfaceSim';
import { DistrictReading, GovernorMove, GovernorPolicy, SiteCorner } from './ResidentGovernor';
import { BRIEF_FACILITY } from './GovernorBrief';

/**
 * What a governor did at one season boundary.
 *
 * The minutes, and the register matters: `issued` says a command was accepted into
 * the log, **not** that it did anything. A `FORCE_PLACE` whose footprint has
 * something standing on it is well-formed, correctly ordered and inert — that is
 * `CommandOutcome.applied`, which arrives a tick later and belongs to whoever is
 * watching the district rather than to whoever issued the command. A minute that
 * claimed otherwise would be the one document in this build that lied about the
 * thing it was written to record.
 */
export interface GovernorMinute {
  /** The season these moves opened. */
  season: number;
  /** What was decided, in the order the policy decided it. */
  moves: readonly GovernorMove[];
  /** Moves the district refused at the door. Non-zero is a bug in a brief. */
  refused: number;
}

/**
 * A governor in the chair over one district.
 *
 * Constructed with the district, an issuer under the governor's actor, and a
 * policy. Driven by whoever owns the clock: the client calls `observe()` once a
 * tick before advancing the simulation, and the scenario harness does the same
 * thing at the same point in the loop.
 */
export class GovernorSeat {
  /**
   * Chronicle lines this seat has already opened a season on.
   *
   * `-1` rather than `0` because a district that has closed no season still has a
   * season *open* — its first — and the polity's opening instructions belong at the
   * top of it. So the first `observe()` opens season 1 against an empty record,
   * which in the shipped brief is a governor with nothing measured and therefore
   * nothing to say.
   */
  private seasonsOpened = -1;

  private readonly log: GovernorMinute[] = [];

  private active = true;

  constructor(
    private readonly sim: SurfaceSim,
    private readonly issuer: CommandIssuer,
    private readonly policy: GovernorPolicy
  ) {}

  /** The policy in the chair. */
  getPolicy(): GovernorPolicy {
    return this.policy;
  }

  /** Every season this seat has decided, oldest first. */
  getMinutes(): readonly GovernorMinute[] {
    return this.log;
  }

  /**
   * Whether this governor is playing.
   *
   * `false` leaves the seat empty: the boundary is still tracked, so switching a
   * governor back on mid-history does not make it act on seasons it slept through.
   * This exists for the author, who has a district and would like to see what
   * happens to it with and without a polity above it — which, per ADR-024 D161, is
   * the *only* honest way to compare two governed histories, because the
   * counterfactual inside one district is unavailable.
   */
  setActive(active: boolean): void {
    this.active = active;
  }

  isActive(): boolean {
    return this.active;
  }

  /**
   * Open a season if the district has closed one since the last look.
   *
   * Called before the district's tick, which is where a mayor's click lands too
   * (ADR-004: commands drain at the head of the tick they are stamped for). A
   * governor whose moves were applied *after* the tick would be a governor acting on
   * a district one tick older than the record it read.
   */
  observe(): void {
    const chronicle = this.sim.getChronicle();
    if (chronicle.length <= this.seasonsOpened) {
      // A shorter or equal Chronicle means a load, a restore, or nothing at all.
      // Re-sync silently: see `resync`.
      this.seasonsOpened = chronicle.length;
      return;
    }
    this.seasonsOpened = chronicle.length;
    if (!this.active) return;
    this.open();
  }

  /**
   * Adopt a district's history without acting on it.
   *
   * A load moves the Chronicle to somewhere this seat has no relationship with, and
   * a governor that then levied a season's worth of decisions would be reacting to
   * seasons that happened to somebody else. The client calls this after restoring a
   * save, for the same reason and in the same line as it resets its own
   * `seasonsSeen`.
   */
  resync(): void {
    this.seasonsOpened = this.sim.getChronicle().length;
  }

  /** Assemble the reading, ask, apply, minute. */
  private open(): void {
    const reading = this.read();
    const moves = this.policy.openSeason(reading);
    let refused = 0;
    for (const move of moves) {
      if (!this.apply(move)) refused++;
    }
    this.log.push({ season: reading.season, moves, refused });
  }

  /**
   * Everything the policy is allowed to see, and nothing else.
   *
   * The one method in the governor module that holds a district, which is what
   * makes the boundary checkable: widening the reading is a diff in this function,
   * under a heading that says why a governor needed to know.
   */
  private read(): DistrictReading {
    return {
      season: this.sim.getSeason(),
      chronicle: this.sim.getChronicle(),
      facilities: this.sim.facilities.count,
      interchanges: this.sim.freight.getInterchanges().length,
      cols: this.sim.grid.getCols(),
      rows: this.sim.grid.getRows(),
      sites: this.availableSites(),
    };
  }

  /**
   * Corners where a facility's footprint would fit today, in grid order.
   *
   * Open ground only: every cell of the rectangle `NATURAL`, nothing built on it,
   * no slab already covering it. That is a narrower pool than §7 allows — *"the
   * governor places the rectangle anywhere"* — and the reason is `SurfaceSim`'s, not
   * this file's: a `FORCE_PLACE` over a standing house is refused today because
   * demolition and the displaced are not built (Q29, roadmap item 5), so a governor
   * that named such a corner would issue a command that did nothing. Offering only
   * corners that can be taken keeps the refusal count in the minutes meaningful.
   *
   * What the pool does **not** filter on is everything a conscience would: the
   * bushes standing on the cells, the covenant a mayor bought over them
   * (`MercyManager.condemn` is what happens then, and the count is the whole
   * consequence), the homes across the road, the portal underneath. A governor
   * offered a shorter list because something lived there would be a governor who
   * could tell.
   */
  private availableSites(): readonly SiteCorner[] {
    const spec = FACILITY_SPECS[BRIEF_FACILITY];
    const cols = this.sim.grid.getCols();
    const rows = this.sim.grid.getRows();
    const sites: SiteCorner[] = [];

    for (const cell of this.sim.grid.getNaturalCells()) {
      if (cell.x + spec.cols > cols || cell.y + spec.rows > rows) continue;
      if (this.footprintIsOpen(cell.x, cell.y, spec.cols, spec.rows)) {
        sites.push({ x: cell.x, y: cell.y });
      }
    }
    return sites;
  }

  private footprintIsOpen(x: number, y: number, cols: number, rows: number): boolean {
    for (let dy = 0; dy < rows; dy++) {
      for (let dx = 0; dx < cols; dx++) {
        const cell = this.sim.grid.getCell(x + dx, y + dy);
        if (!cell || cell.state !== CellState.NATURAL) return false;
        if (this.sim.facilities.covers(x + dx, y + dy) !== null) return false;
        if (this.sim.houses.occupiesCell(x + dx, y + dy)) return false;
      }
    }
    return true;
  }

  /**
   * Send one move to the district, through the channel that move belongs in.
   *
   * Returns whether the district took it. For a verb that is whether the command
   * was *accepted*, which is all an issuer can know: see `GovernorMinute`.
   */
  private apply(move: GovernorMove): boolean {
    switch (move.kind) {
      case 'prices':
        // A posting that moves nothing returns false, and that is not a refusal —
        // it is a market repeating itself. Reported as taken, because it was.
        this.sim.postMarket({
          computePrice: move.computePrice,
          goodsPrice: move.goodsPrice,
          bondYield: move.bondYield,
        });
        return true;
      case 'tithe':
        return this.issuer.setTithe(move.rate).ok;
      case 'site':
        return this.issuer.forcePlace(move.facility, move.x, move.y).ok;
      case 'freight':
        this.sim.setFreight(move.legsPerCycle);
        return true;
      default: {
        // Exhaustiveness, checked by the compiler rather than by a list somebody
        // maintains: a new move kind that nobody taught this switch about does not
        // compile.
        const unreachable: never = move;
        return unreachable;
      }
    }
  }
}

/**
 * The issuer a resident governor speaks through.
 *
 * A second actor in a world that used to have one, under `LOCAL_GOVERNOR`, and it
 * needs its own because `(actor, seq)` is how the queue recognises a
 * retransmission: two issuers sharing an actor would collide and one of them would
 * have its commands discarded as duplicates. `'governor'` sorts before `'mayor'`,
 * so a seizure and a pave stamped for the same tick apply in that order on every
 * peer — which is what `compareCommands` was written for.
 */
export function governorIssuer(sim: SurfaceSim): CommandIssuer {
  return new CommandIssuer(sim, LOCAL_GOVERNOR);
}
